// turnEventPipeline.js — E1-WS1-T03 (refactor-deepening / F1, Event/Stream Pipeline seam).
//
// Deep module owning the run's EVENT/STREAM PIPELINE — the ~90-line, ~26-decision-point
// `onEvent` body that lived inside SessionWorkspace.runAgent, plus the per-turn feed
// state it mutates and the post-loop code reads back. Interface: a small
//   createTurnEventPipeline(deps) → { feed(evt), flush(), toolRecords, finalReasoning,
//                                     lastVerificationReport, lastAgentMsgId }
// hides a lot of behavior: the streaming bubble (coalesced token/reasoning deltas), the
// tool_call/tool_result card lifecycle (running → done/denied, duration, records), the
// verification seal, the loop_stop/auto_review/auto_correct/loop_step side-channels, and
// the error/stream_truncated surface.
//
// Deletion test (01-DOCTRINE): delete this module and the whole evt→UI mapping + the
// stream/tool bookkeeping (toolMsgIds/toolStarts/toolRecords/streamMsg/finalReasoning/
// lastAgentMsgId/lastVerificationReport + ensureStreamMsg/finalizeStreamMsg + deltaFlow)
// scatter back into runAgent — the biggest concentration in the god-function. DEEP.
//
// Behavior byte-identical: the branch logic is the former inline onEvent verbatim, only
// reorganized into one small handler per event type behind a dispatch table (so no single
// function trips the complexity ratchet — the same spirit as deep modules). All handlers
// operate on a shared mutable `ctx` (deps + turn state). The lifecycle preamble
// (abort-guard + watchdog ping) is injected (isAborted/onActivity) so the pipeline stays
// the single evt entry point. PURE helpers imported; only component-scoped EFFECTS injected.

import { createTurnDeltaFlow } from '../../../lib/loop/deltaCoalescer.js';
import { loopStopObservation } from '../../../lib/eventTypes.js';
import { handleHarnessSignal } from './harnessSignalWire.js';
import { appendCapped } from '../../../lib/loop/liveStreamCap.js';
import { capStored } from '../../../lib/loop/capStored.js';
import { emitVoiceDelta } from '../../../lib/voiceDeltaBridge.js';
import { describeToolCall } from '../../../lib/agentTools.js';
import { advanceLiveStatus } from '../chat/LiveActivity.jsx';
import { surfaceTurnEvent } from '../../../lib/loop/turnErrorSurface.js';
import { readVerificationReportEnabled } from '../../../lib/flags/sprintFlags.js';
import { deriveVerificationReport } from '../../../lib/verificationSeal.js';
import { recordVerification } from '../../../lib/verificationLedger.js';

// ——— streaming bubble helpers (mutate ctx.streamMsg / ctx.lastAgentMsgId) ———
function ensureStreamMsg(ctx) {
  if (!ctx.streamMsg) {
    const id = ctx.pushMsg({ role: 'agent', model: ctx.route.model, content: '', reasoning: '', streaming: true });
    ctx.streamMsg = { id, content: '', reasoning: '' };
    ctx.lastAgentMsgId = id;
  }
  return ctx.streamMsg;
}
function finalizeStreamMsg(ctx, content, reasoning) {
  if (!ctx.streamMsg) return false;
  ctx.updateMsg(ctx.streamMsg.id, {
    content: content != null ? content : ctx.streamMsg.content,
    reasoning: reasoning != null ? reasoning : ctx.streamMsg.reasoning,
    streaming: false,
  });
  ctx.streamMsg = null;
  return true;
}

// ——— one handler per main event type (the former onEvent if/else-if branches) ———
function handleDelta(ctx, evt) {
  // Delta de streaming: acumula na bolha viva; o store recebe 1 flush/frame (E5-WS1).
  const s = ensureStreamMsg(ctx);
  // WSX — cap no acumulador VIVO (evita RAM sem-fim; final persistido vem de evt.content).
  if (evt.type === 'token') { s.content = appendCapped(s.content, evt.delta); emitVoiceDelta(evt, s.content); }
  else { s.reasoning = appendCapped(s.reasoning, evt.delta); ctx.finalReasoning = s.reasoning; }
  ctx.deltaFlow.onDelta(s, evt.type);
}
function handleAssistant(ctx, evt) {
  if (!evt.content) return; // era `else if (assistant && content)` — sem content, no-op
  // Fecha a bolha de streaming (se houve) com o texto/raciocínio final;
  // senão (sem streaming, ex.: cloud) cria a mensagem do zero.
  if (evt.reasoning) ctx.finalReasoning = evt.reasoning;
  if (!finalizeStreamMsg(ctx, evt.content, evt.reasoning)) {
    ctx.lastAgentMsgId = ctx.pushMsg({ role: 'agent', content: evt.content, reasoning: evt.reasoning || '', model: ctx.route.model });
  }
}
function handleToolCall(ctx, evt) {
  // Um tool encerra o segmento de geração: fecha a bolha viva (pode ter só raciocínio).
  finalizeStreamMsg(ctx);
  // present_plan é a tool TERMINAL do Plan Mode — seu payload já aparece no
  // PlanProposalCard, então NÃO renderizamos um ToolCallBlock redundante. Só fecha a
  // bolha viva; o card cuida do resto.
  if (evt.name === 'present_plan') { ctx.setLiveStatus({ phase: 'pensando' }); return; }
  ctx.setLiveStatus((p) => advanceLiveStatus(p, evt)); // fix I free-form: mantém o passo N
  // Nasce 'running'; o tool_result correspondente preenche o resto.
  const uiId = ctx.pushMsg({
    role: 'tool',
    tool_name: evt.name,
    tool_args: evt.args || {},
    content: describeToolCall(evt.name, evt.args),
    status: 'running',
  });
  if (evt.id != null) ctx.toolMsgIds.set(evt.id, uiId);
  ctx.toolStarts.set(uiId, Date.now());
  ctx.lastToolMsgId = uiId;
  ctx.toolRecords.set(uiId, { tool_name: evt.name, tool_args: evt.args || {}, content: describeToolCall(evt.name, evt.args) });
}
function handleToolResult(ctx, evt) {
  if (evt.name === 'present_plan') {
    // Contraparte do skip acima — sem card; o PlanProposalCard assume.
    ctx.setLiveStatus({ phase: 'pensando' });
    return;
  }
  const denied = evt.approved === false;
  const uiId = (evt.id != null && ctx.toolMsgIds.get(evt.id)) || ctx.lastToolMsgId;
  const startedAt = uiId ? ctx.toolStarts.get(uiId) : null;
  const durationMs = startedAt ? Date.now() - startedAt : undefined;
  const patch = denied
    ? { status: 'denied', result: 'Ação negada pelo usuário.', duration_ms: durationMs }
    : { status: 'done', result: capStored(String(evt.result ?? '')), duration_ms: durationMs };
  if (uiId) {
    ctx.updateMsg(uiId, patch);
    const rec = ctx.toolRecords.get(uiId);
    if (rec) Object.assign(rec, { status: patch.status, result: patch.result, duration_ms: patch.duration_ms });
    if (evt.id != null) ctx.toolMsgIds.delete(evt.id);
    ctx.toolStarts.delete(uiId);
    ctx.lastToolMsgId = null;
  } else {
    const fbId = ctx.pushMsg({ role: 'tool', tool_name: evt.name, tool_args: {}, content: describeToolCall(evt.name, {}), ...patch });
    ctx.toolRecords.set(fbId, { tool_name: evt.name, tool_args: {}, content: describeToolCall(evt.name, {}), status: patch.status, result: patch.result, duration_ms: patch.duration_ms });
  }
  // Voltou de uma tool → o próximo passo costuma ser pensar de novo.
  ctx.setLiveStatus({ phase: 'pensando' });
}
function handleAutoCorrect(ctx, evt) {
  // E2 (super-marathon) — DERIVE the verification seal from the oracle verdicts the spine
  // carries on auto_correct. Flag-gated: OFF ⇒ null ⇒ byte-identical no-op.
  const rep = readVerificationReportEnabled() ? deriveVerificationReport(evt.result) : null;
  if (rep) { ctx.lastVerificationReport = rep; recordVerification(rep); }
}
function handleLoopStep(ctx, evt) {
  ctx.setLiveStatus((p) => advanceLiveStatus(p, evt)); // fix I free-form: "Passo N · …"
}
function handleErrorEvent(ctx, evt) {
  // E2-WS3 — superfície única (helper puro): erro do loop + truncamento visível.
  surfaceTurnEvent(evt, { setError: ctx.setError, pushMsg: ctx.pushMsg, emitToast: (detail) => window.dispatchEvent(new CustomEvent('apolo:toast', { detail })) });
}

// Dispatch table — exactly the former if/else-if chain (loop_stop/auto_review are pure
// side-channels handled in feedEvent, so they are NOT here).
const MAIN_HANDLERS = {
  token: handleDelta,
  reasoning: handleDelta,
  assistant: handleAssistant,
  tool_call: handleToolCall,
  tool_result: handleToolResult,
  auto_correct: handleAutoCorrect,
  loop_step: handleLoopStep,
  error: handleErrorEvent,
  stream_truncated: handleErrorEvent,
};

function feedEvent(ctx, evt) {
  // Parada imediata: após abort, ignora eventos tardios do loop.
  if (ctx.isAborted()) return;
  // WS-B — atividade do loop reseta o watchdog idle (só dispara em silêncio total).
  ctx.onActivity();
  // E5-WS1 — evento não-delta: aterrissa o texto pendente ANTES do próximo card.
  if (evt.type !== 'token' && evt.type !== 'reasoning') ctx.deltaFlow.flush();
  if (evt.type === 'loop_stop') {
    ctx.observe('agent_loop_stop', loopStopObservation(evt)); // T5 — payload c/ tool+failing
    ctx.setRunAgentMeta(ctx.session.id, { stopReason: evt.stopReason });
  }
  // W3 — qualidade da ENTREGA vira sinal de learning (counts-only, helper puro).
  handleHarnessSignal(evt, ctx.session, ctx.observe, ctx.noteEditedPaths);
  ctx.keepEval.onLoopEvent(evt); // E5/D — Keep Rate (filtra write/edit no store)
  // S4 (E5) — auto-review decisions ao painel Guard-rails (canal próprio).
  if (evt.type === 'auto_review') ctx.dispatchAutoReview(ctx.session.id, evt);
  MAIN_HANDLERS[evt.type]?.(ctx, evt);
}

/**
 * @param {{
 *   route: { model: string }, session: object,
 *   pushMsg: (msg: object) => string, updateMsg: (id: string, patch: object) => void,
 *   setLiveStatus: Function, setError: (t: string) => void,
 *   observe: Function, noteEditedPaths: Function, dispatchAutoReview: Function,
 *   setRunAgentMeta: Function, keepEval: { onLoopEvent: (evt: object) => void },
 *   onActivity: () => void, isAborted: () => boolean,
 * }} deps
 */
export function createTurnEventPipeline(deps) {
  // ctx = deps + a mutable turn-feed state the handlers own; getters expose what the
  // runAgent reads back after the loop (persist / verification seal / self-repair / telemetria).
  const ctx = {
    ...deps,
    toolMsgIds: new Map(),
    toolStarts: new Map(),
    toolRecords: new Map(), // uiId -> { tool_name, tool_args, content, status, result, duration_ms }
    lastToolMsgId: null,
    lastVerificationReport: null,
    lastAgentMsgId: null,
    streamMsg: null, // { id, content, reasoning }
    finalReasoning: '', // HRN3-T01 — last reasoning, persisted as a summary
    deltaFlow: null,
  };
  // E5-WS1 — deltas coalescidos por frame: 1 write no store por frame em vez de 1/token.
  ctx.deltaFlow = createTurnDeltaFlow({ updateMsg: deps.updateMsg, setLiveStatus: /** @type {any} */ (deps.setLiveStatus) });

  return {
    feed: (evt) => feedEvent(ctx, evt),
    flush: () => ctx.deltaFlow.flush(),
    get toolRecords() { return ctx.toolRecords; },
    get finalReasoning() { return ctx.finalReasoning; },
    get lastVerificationReport() { return ctx.lastVerificationReport; },
    get lastAgentMsgId() { return ctx.lastAgentMsgId; },
  };
}
